import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, Check, BellRing, Volume2, VolumeX } from 'lucide-react';
import useNotificationAudio from '../../hooks/useNotificationAudio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_LABEL = {
  waitlist_signup_received: 'Waitlist signup',
  new_prospect_joined_member_waitlist: 'New prospect',
  prospect_claim_started: 'PIF claim started',
  prospect_claimed_by_pif: 'PIF complete',
  phase2_unlocked: 'Phase 2 unlocked',
  tier_changed: 'Tier change',
  credits_granted: 'Credits granted',
  credits_used: 'Credits used',
  admin_announcement_posted: 'Announcement',
};

const MemberNotifications = ({ inDashboard = true }) => {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const { manifest, play, reload: reloadManifest } = useNotificationAudio(token);
  const lastTopIdRef = useRef(null);

  const audioMuted = manifest && !manifest.enabled;

  const toggleAudioMute = async () => {
    try {
      await axios.put(
        `${API}/member/preferences/notifications`,
        { audio_enabled: !manifest?.user?.audio_enabled },
        { headers: auth },
      );
      reloadManifest();
      toast.success(manifest?.user?.audio_enabled ? 'Audio muted' : 'Audio unmuted');
    } catch (e) {
      toast.error('Failed to update audio preference');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/member/notifications`, {
        headers: auth,
        params: { unread_only: unreadOnly, limit: 50 },
      });
      const newItems = res.data.items || [];
      setItems(newItems);
      setUnreadCount(res.data.unread_count ?? 0);

      // Trigger sound on new top-of-list event
      const topId = newItems[0]?.id;
      if (lastTopIdRef.current && topId && topId !== lastTopIdRef.current && newItems[0]?.read === false) {
        play(newItems[0].event);
      }
      if (topId) lastTopIdRef.current = topId;
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [unreadOnly]);

  const markAll = async () => {
    try {
      await axios.post(`${API}/member/notifications/mark-read`, {}, { headers: auth });
      toast.success('All notifications marked as read');
      load();
    } catch (e) {
      toast.error('Failed to mark notifications');
    }
  };

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/member/notifications/mark-read`, { notification_ids: [id] }, { headers: auth });
      load();
    } catch (e) {/* silent */}
  };

  return (
    <Card data-testid="member-notifications">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {unreadCount > 0 ? <BellRing className="h-5 w-5 text-amber-500" /> : <Bell className="h-5 w-5" />}
              Notifications
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2" data-testid="unread-badge">
                  {unreadCount} unread
                </Badge>
              )}
            </CardTitle>
            <CardDescription>System events & alerts.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAudioMute}
              data-testid="toggle-audio-btn"
              title={audioMuted ? 'Audio muted' : 'Audio on'}
            >
              {audioMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
            </Button>
            <Button
              variant={unreadOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUnreadOnly(!unreadOnly)}
              data-testid="toggle-unread-only-btn"
            >
              {unreadOnly ? 'All' : 'Unread only'}
            </Button>
            <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read-btn">
              <Check className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground" data-testid="empty-notifications">
            {unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
          </div>
        ) : (
          <div className="space-y-2" data-testid="notifications-list">
            {items.map((n) => (
              <div
                key={n.id}
                className={`border rounded p-3 ${n.read ? 'opacity-70' : 'bg-amber-50/30 dark:bg-amber-950/10'}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{n.title}</span>
                      <Badge variant="outline" className="text-xs">{EVENT_LABEL[n.event] || n.event}</Badge>
                      {!n.read && <Badge variant="default" className="text-xs">NEW</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{n.body}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && (
                    <Button size="sm" variant="ghost" onClick={() => markOne(n.id)} data-testid={`mark-read-${n.id}`}>
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MemberNotifications;
